import { MEDIA_LIMITS } from '@cattle/money'
import { z } from 'zod'

import {
  bytesSchema,
  idSchema,
  isoInstantSchema,
  mediaKindSchema,
  mediaStatusSchema,
  mediaVariantSchema,
  noteSchema,
  versionSchema,
} from '../common'

/**
 * A40–A42 · Media.
 *
 * A photo of the animal is how the trader recognises the row — the tag number
 * is a label, the face is the animal. So media is part of the animal payload,
 * not an afterthought.
 *
 * Bytes never travel as JSON. A40 is `multipart/form-data`, streamed to
 * storage and **magic-byte sniffed** (a `.jpg` that is really a video is a
 * `415`, whatever the client declared). The schema below describes the
 * accompanying fields and what comes back.
 */

/** Republished from `@cattle/money` so a client can pre-check before uploading. */
export const MEDIA_UPLOAD_LIMITS = MEDIA_LIMITS

export const mediaItemSchema = z.object({
  id: idSchema,
  animalId: idSchema,
  kind: mediaKindSchema,
  status: mediaStatusSchema,
  caption: noteSchema.nullish(),
  isCover: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  byteSize: bytesSchema,
  width: z.number().int().positive().nullish(),
  height: z.number().int().positive().nullish(),
  /** Videos only. */
  durationSeconds: z.number().nonnegative().nullish(),
  hasTranscode: z.boolean(),
  /**
   * Relative paths to `GET /media/:id/:variant`. Never a public URL and never
   * a signed one that outlives the session — a photo of a man's herd is his
   * (conflict C6). The client requests them with its own credentials.
   */
  variants: z.array(mediaVariantSchema),
  failureReason: z.string().nullish(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

/* ── A40 · POST /media/upload (multipart) ─────────────────────────────────── */

/**
 * The non-file fields of the multipart body. Values arrive as text, hence the
 * coercion on the boolean.
 */
export const mediaUploadFields = z.object({
  animalId: idSchema,
  kind: mediaKindSchema,
  caption: noteSchema.optional(),
  isCover: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
    .optional(),
})

export const mediaUploadResponse = mediaItemSchema

/* ── A41 · GET /media/:id/:variant ────────────────────────────────────────── */

/**
 * Binary, not JSON: `206 Range` is mandatory (a 200MB video on a 3G link must
 * be seekable), with an ETag and an immutable cache header. Only the path
 * parameters are contract.
 */
export const mediaVariantParams = z.object({
  id: idSchema,
  variant: mediaVariantSchema,
})

/* ── A42 · PATCH /media/:id · DELETE /media/:id ───────────────────────────── */

/**
 * Cover, caption, order. Setting `isCover` true clears it on the animal's other
 * media — at most one cover per animal is a partial unique index, so the server
 * does both sides in one transaction rather than returning a `409`.
 */
export const mediaPatchRequest = z
  .object({
    caption: noteSchema.nullish(),
    isCover: z.boolean().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export type MediaItem = z.infer<typeof mediaItemSchema>
export type MediaUploadFields = z.input<typeof mediaUploadFields>
export type MediaPatchRequest = z.input<typeof mediaPatchRequest>
export type MediaVariantParams = z.input<typeof mediaVariantParams>
